import { useEffect, useState } from 'react';
import { useAuth } from '../api/useAuth';
import { api } from '../api/client';
import { Stack, Row, Text, Avatar, Panel, List, ListItem, Button, Tag, EmptyState, Input, Card, Divider } from '../ui';

export function Hangs() {
  const { friend, refresh } = useAuth();
  const [nearby, setNearby] = useState<{ me: any; nearby: any[] } | null>(null);
  const [hangs, setHangs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [suggestion, setSuggestion] = useState<{ for: string[]; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [planForm, setPlanForm] = useState<{ city: string; venue: string; when: string } | null>(null);
  const sharing = (friend as any)?.locationSharing ?? 'off';

  async function load() {
    setLoading(true);
    try {
      const [n, h] = await Promise.all([api.nearby(), api.hangs()]);
      setNearby(n); setHangs(h.hangs);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function turnOn() {
    await api.setLocationSharing('city');
    await refresh();
    // Force a location push immediately
    try { await api.pushLocation({ source: 'ip' }); } catch {}
    load();
  }

  async function suggestMeetup(friendIds: string[]) {
    setBusy(true); setSuggestion(null);
    try {
      const names = friendIds.map(id => nearby?.nearby.find(f => f.id === id)?.name).filter(Boolean).join(' and ');
      const intent = `i'm in ${nearby?.me?.city}, ${names} ${friendIds.length === 1 ? 'is' : 'are'} too. suggest one specific dinner or drinks plan: a venue, a time tonight or tomorrow, and one short reason it works for our group.`;
      const r = await api.agentChat(intent);
      setSuggestion({ for: friendIds, text: r.reply });
      // Pre-fill the plan form with the city
      setPlanForm({ city: nearby?.me?.city ?? '', venue: '', when: defaultWhen() });
    } finally { setBusy(false); }
  }

  function defaultWhen() {
    const d = new Date();
    d.setHours(19, 30, 0, 0);
    if (d.getTime() < Date.now()) d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0,16);
  }

  async function createHang() {
    if (!planForm || !suggestion) return;
    setBusy(true);
    try {
      const startsAt = new Date(planForm.when).getTime();
      const me = friend!.id;
      const attendees = [me, ...suggestion.for];
      await api.createHang(planForm.city, startsAt, attendees, planForm.venue, suggestion.text);
      setSuggestion(null); setPlanForm(null);
      load();
    } finally { setBusy(false); }
  }

  return (
    <Stack gap={3}>
      <Row justify="between" align="center">
        <Stack gap={0.5}>
          <Text variant="display">Hangs</Text>
          <Text muted variant="small">live overlap detection across your circle</Text>
        </Stack>
        <Row gap={1}>
          {sharing === 'off' ? (
            <Button variant="primary" size="sm" className="h-11 sm:h-9" onClick={turnOn}>turn on location</Button>
          ) : (
            <Tag>sharing: {sharing}</Tag>
          )}
        </Row>
      </Row>

      {sharing === 'off' && (
        <Card>
          <Stack gap={1}>
            <Text variant="heading">location sharing is off</Text>
            <Text variant="small" muted>turn it on to see who's in your city right now and get auto-suggestions for meetups. you can choose city-only or precise gps.</Text>
            <Row gap={1}><Button variant="primary" onClick={turnOn}>turn on (city only)</Button></Row>
          </Stack>
        </Card>
      )}

      {sharing !== 'off' && (
        <Panel title="In your city right now">
          {loading ? <Text variant="label">loading…</Text> : (() => {
            const inMyCity = nearby?.nearby.filter(f => f.sameCityAsMe) ?? [];
            return inMyCity.length === 0
              ? <EmptyState title="no one in your circle is in this city right now" hint="when a friend shows up here, you'll see them and get a notification" />
              : <Stack gap={2}>
                  {inMyCity.map(f => (
                    <Row key={f.id} gap={2} align="center" justify="between">
                      <Row gap={2} align="center">
                        <Avatar name={f.name} friendId={f.id} hasAvatar={!!f.avatarUrl} size="md" />
                        <Stack gap={0.5}>
                          <Text variant="small" className="font-medium">{f.name}</Text>
                          <Text variant="label">in {f.city}{f.country ? `, ${f.country}` : ''} · last update {new Date(f.at).toLocaleString()}</Text>
                        </Stack>
                      </Row>
                      <Button variant="primary" size="sm" disabled={busy} onClick={() => suggestMeetup([f.id])}>
                        suggest meetup
                      </Button>
                    </Row>
                  ))}
                  {inMyCity.length >= 2 && (
                    <Row justify="end" className="mt-1">
                      <Button variant="secondary" size="sm" disabled={busy} onClick={() => suggestMeetup(inMyCity.map(f => f.id))}>
                        suggest a group meetup ({inMyCity.length} friends)
                      </Button>
                    </Row>
                  )}
                </Stack>;
          })()}
        </Panel>
      )}

      {sharing !== 'off' && (
        <Panel title="Across your circle">
          {(nearby?.nearby ?? []).length === 0
            ? <EmptyState title="no one is sharing location yet" hint="invite the friends you want to track overlaps with" />
            : <List>
                {(nearby?.nearby ?? []).filter(f => !f.sameCityAsMe).map(f => (
                  <ListItem key={f.id}>
                    <Row gap={2} justify="between" align="center">
                      <Row gap={2} align="center">
                        <Avatar name={f.name} friendId={f.id} hasAvatar={!!f.avatarUrl} size="sm" />
                        <Stack gap={0.5}>
                          <Text variant="small">{f.name}</Text>
                          <Text variant="label">{f.city}{f.country ? `, ${f.country}` : ''}</Text>
                        </Stack>
                      </Row>
                      <Text variant="label">{new Date(f.at).toLocaleString()}</Text>
                    </Row>
                  </ListItem>
                ))}
              </List>}
        </Panel>
      )}

      {suggestion && planForm && (
        <Panel title="agent suggestion">
          <Stack gap={2}>
            <Card className="bg-whisper"><Text variant="small" className="whitespace-pre-wrap">{suggestion.text}</Text></Card>
            <Stack gap={1}>
              <Text variant="micro">refine and confirm</Text>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Stack gap={0.5}>
                  <Text variant="label">city</Text>
                  <Input value={planForm.city} onChange={e => setPlanForm({ ...planForm, city: e.target.value })} />
                </Stack>
                <Stack gap={0.5}>
                  <Text variant="label">venue</Text>
                  <Input value={planForm.venue} onChange={e => setPlanForm({ ...planForm, venue: e.target.value })} placeholder="extract from suggestion" />
                </Stack>
                <Stack gap={0.5}>
                  <Text variant="label">when</Text>
                  <Input type="datetime-local" value={planForm.when} onChange={e => setPlanForm({ ...planForm, when: e.target.value })} />
                </Stack>
              </div>
              <Row gap={1}>
                <Button variant="primary" disabled={busy || !planForm.city || !planForm.when} onClick={createHang}>{busy ? 'creating…' : 'create hang'}</Button>
                <Button variant="ghost" onClick={() => { setSuggestion(null); setPlanForm(null); }}>discard</Button>
              </Row>
            </Stack>
          </Stack>
        </Panel>
      )}

      <Panel title="Your hangs">
        {hangs.length === 0
          ? <EmptyState title="no hangs yet" hint="they show up here once you create one" />
          : <List>
              {hangs.map(h => (
                <ListItem key={h.id}>
                  <Stack gap={1}>
                    <Row gap={1}>
                      <Text variant="small" className="font-medium">{h.city}</Text>
                      <Tag>{new Date(h.startsAt).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</Tag>
                      <Tag>{h.status}</Tag>
                    </Row>
                    {h.venue && <Text variant="label">{h.venue}</Text>}
                    {h.notes && <Text variant="small" muted className="whitespace-pre-wrap">{h.notes}</Text>}
                  </Stack>
                </ListItem>
              ))}
            </List>}
      </Panel>
    </Stack>
  );
}
